import React from "react";
import { Popup } from "react-map-gl";
import { Button } from "antd";
import Icon, { ArrowRightOutlined } from "@ant-design/icons";
import { round, omit, sumBy } from "lodash";
import { ReactSVG } from "react-svg";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import "./Popup.scss";

import markerIcons from "../Markers/markerIcons";
import { STATUS, COLOR_DEFAULT } from "../../constants";
import { convertMinutesToTime, getTranslation } from "../../utils/helper";
import { convertToInt } from "../RouteChart/Utils";
import LabelComponent from "../Elements/LabelComponent";

const defaultColor = COLOR_DEFAULT;

export const PopupStopUnassigned = ({ stop, onClosePopup, clickShowMoreStop, setting }) => {
  return (
    <Popup
      key={`stop-${stop.id}`}
      longitude={stop.lng}
      latitude={stop.lat}
      closeButton={true}
      closeOnClick={false}
      onClose={onClosePopup}
      anchor="bottom"
      offsetTop={-32}
      offsetLeft={0}
      dynamicPosition={false}>
      <div style={{ maxWidth: 400 }}>
        <div className="order-popup">
          <p>
            [{stop.type === "pickup" ? "P" : "D"}] {stop.address} <br />
            {!!stop?.unscheduledReason && <b style={{ color: "red" }}>{stop?.unscheduledReason}</b>}
          </p>
          <div className="actions">
            <span className="show-more" onClick={() => clickShowMoreStop(stop)} style={{ margin: 0 }}>
              {getTranslation(setting, "Common", "Show more")} {IconArrowRight}
            </span>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export const PopupRoute = ({ routeClicked, onClosePopup, clickRoute, setting, type = "start" }) => {
  return (
    <Popup
      key={`route-${routeClicked.driver?._id}`}
      longitude={type === "start" ? routeClicked.startLng : routeClicked.endLng}
      latitude={type === "start" ? routeClicked.startLat : routeClicked.endLat}
      closeButton={true}
      closeOnClick={false}
      onClose={onClosePopup}
      anchor="bottom"
      offsetTop={type === "start" ? -18 : -38}
      offsetLeft={0}
      dynamicPosition={false}>
      <div className="order-popup">
        {type === "start" ? routeClicked.startAddress : routeClicked.endAddress}
        {type === "start" && (
          <div className="actions">
            <span className="show-more" onClick={clickRoute} style={{ margin: 0 }}>
              {getTranslation(setting, "Common", "Show more")} <ArrowRightOutlined />
            </span>
          </div>
        )}
      </div>
    </Popup>
  );
};

export const PopupStop = ({ stopClicked, onClosePopup, getContentStop }) => {
  return (
    <Popup
      key={`stop-${stopClicked.id}`}
      longitude={stopClicked.lng}
      latitude={stopClicked.lat}
      closeButton={true}
      closeOnClick={false}
      onClose={onClosePopup}
      anchor="bottom"
      offsetTop={-32}
      offsetLeft={0}
      dynamicPosition={false}>
      <div style={{ maxWidth: 550 }}>{getContentStop(stopClicked)}</div>
    </Popup>
  );
};

export const ContentStop = ({
  stop,
  onClickStop,
  getStopLeft,
  unassignedStops,
  clickShowMoreStop,
  stepClicked: step,
  stops,
  setting,
  order,
}) => {
  const travelMins = step?.travelMins || "-";
  const serviceMins = step?.duration || "-";
  const waitingMins = step?.waitingMins || "-";
  const workingMins = step?.workingMins || "-";
  const distance = step?.distance ? round(step?.distance / 1000, 2) : "-";
  const status = STATUS.find(s => s.code === stop?.status);
  const driverName = stop?.driverName ?? "Driver";
  return (
    <div className="order-popup">
      <div className="header">
        <div className="left">{stop.orderCode}</div>
        <div className="right">
          {driverName && (
            <div className="driverInfo">
              <img src={markerIcons["driver"][stop.color]} alt={driverName} />
              {driverName}
            </div>
          )}
        </div>
      </div>
      <div className="content-top">
        <div className="type">{getTranslation(setting, "Orders", stop.type)}</div>
        <span className={`status orderStatus ${status?.code?.toLowerCase()}`}>
          {getTranslation(setting, "Orders", status?.name ?? "N/A")}
        </span>
        <div className="name">{stop.name}</div>
      </div>
      <div className="content-address">{stop.address}</div>
      <div className="content-main">
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: "25%" }}>{getTranslation(setting, "Orders", "Time Window")}</th>
              {step ? (
                <>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Orders", "Arrival Time")}</th>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Orders", "Finish Time")}</th>
                </>
              ) : (
                <th style={{ width: "25%" }}>{getTranslation(setting, "Orders", "Load")}</th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{(stop.startTime || "") + " - " + (stop.endTime || "")}</td>
              {step ? (
                <>
                  <td>{step?.arrivalTime}</td>
                  <td>{step?.finishTime}</td>
                </>
              ) : (
                <td>{stop.load}</td>
              )}
            </tr>
          </tbody>
        </table>
        {step && (
          <>
            <table style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Orders", "Distance")}</th>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Orders", "Travel Time")}</th>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Orders", "Service Time")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{distance} km</td>
                  <td>{convertMinutesToTime(travelMins)}</td>
                  <td>{convertMinutesToTime(serviceMins)}</td>
                </tr>
              </tbody>
            </table>
            <table style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Orders", "Working Time")}</th>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Orders", "Waiting Time")}</th>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Orders", "Load")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{convertMinutesToTime(workingMins)}</td>
                  <td>{convertMinutesToTime(waitingMins)}</td>
                  <td>{stop.load}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}
        {order?.requiredSkills?.length > 0 && (
          <div className="OrderTypes">
            {order?.requiredSkills.map(type => (
              <div key={type} className="Type">
                {type}
              </div>
            ))}
          </div>
        )}
        {order?.labels?.length > 0 && (
          <div style={{ paddingBottom: "8px" }}>
            <LabelComponent labelIds={order?.labels || []} isEdit={false} />
          </div>
        )}
      </div>
      <div className="actions">
        {stop.routePlanType === "PDP" && (
          <Button
            onClick={() => {
              const stopT = getStopLeft(stop, stops);
              onClickStop(stopT);
              if (unassignedStops.includes(stopT)) {
                clickShowMoreStop(stopT);
              }
            }}>
            {stop.type === "pickup"
              ? getTranslation(setting, "Common", "Go to Delivery")
              : getTranslation(setting, "Common", "Go to Pickup")}{" "}
            <Icon component={MoveToInbox} />
          </Button>
        )}
        <a href={`/orders/${stop.orderId}`} target="_blank" rel="noopener noreferrer" className="link-details">
          {getTranslation(setting, "Common", "View Order Details")} {IconDetailOrder}
        </a>
      </div>
    </div>
  );
};

export const ContentRoute = ({
  routeClicked,
  onClickStop,
  routes,
  canEdit = true,
  setting,
  steps,
  onDuplicateDriver,
}) => {
  const driver = routeClicked.driver;
  const urlIcon = markerIcons["driver"][driver?.color];
  const length = routes[driver._id]?.length;

  const newSteps = routes[driver?._id].map(stop => {
    const step = steps.find(s => stop.orderId === s.orderId && s.type.toLowerCase() === stop.type);
    return { ...step, ...stop };
  });

  const travelMins = round(sumBy(newSteps, "travelMins"), 2) || "-";
  const serviceMins = round(sumBy(newSteps, "duration"), 2) || "-";
  const waitingMins = round(sumBy(newSteps, "waitingMins"), 2) || "-";
  const workingMins = round(sumBy(newSteps, "workingMins"), 2) || "-";
  const distance = round(sumBy(newSteps, "distance") / 1000, 2) || "-";
  const workingPercentage = routeClicked.summary?.workingPercentage || "-";

  const handleDownloadSolution = async () => {
    const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";
    const solutionData = routes[driver._id].map(stop => omit(stop, "id"));
    const wsSolution = XLSX.utils.json_to_sheet(solutionData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSolution, "Solution");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(dataBlob, `${driver?.name?.replace(/\s+/g, "-")}-solution-${Date.now()}` + fileExtension);
  };

  return (
    <div className="content-info-windown info-route">
      <div className="content-header">
        {!canEdit && (
          <div className="content-title">
            <div className="title">
              <img src={urlIcon} alt="" />
              {driver?.name}
            </div>
          </div>
        )}
        {canEdit && (
          <>
            <div className="content-title">
              <div className="title">
                {driver?.name && (
                  <>
                    <img src={urlIcon} alt="" />
                    {driver.name}
                  </>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Button className="btn-download" onClick={() => onDuplicateDriver(driver)}>
                  {getTranslation(setting, "Common", "Duplicate Route")}
                </Button>
                <Button className="btn-download" onClick={handleDownloadSolution}>
                  {getTranslation(setting, "Route Plans", "Download Solution")}
                  <ReactSVG className="icon" src="/icons/download.svg" />
                </Button>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Drivers", "Start Location")}</th>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Drivers", "Time Started First Job")}</th>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Drivers", "End Location")}</th>
                  <th style={{ width: "25%" }}>{getTranslation(setting, "Drivers", "Time Ended Last Job")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className="address">{getTranslation(setting, "Orders", routeClicked.startAddress)}</div>
                  </td>
                  <td>{routes[driver?._id][0]?.arrivalTime}</td>
                  <td>
                    <div className="address">{routeClicked.endAddress}</div>
                  </td>
                  <td>{routes[driver?._id][length - 1]?.finishTime}</td>
                </tr>
              </tbody>
            </table>
            <div
              style={{
                borderTop: "1px solid #E0E0E0",
                margin: "10px 0",
              }}
            />
            <table>
              <thead>
                <tr>
                  <th style={{ width: "12.5%" }}>{getTranslation(setting, "Drivers", "Time Shift")}</th>
                  <th style={{ width: "12.5%" }}>{getTranslation(setting, "Route Plans", "# of Stops")}</th>
                  <th style={{ width: "12.5%" }}>{getTranslation(setting, "Route Plans", "Distance")}</th>
                  <th style={{ width: "12.5%" }}>{getTranslation(setting, "Route Plans", "Travel Time")}</th>
                  <th style={{ width: "12.5%" }}>{getTranslation(setting, "Route Plans", "Service Time")}</th>
                  <th style={{ width: "12.5%" }}>{getTranslation(setting, "Route Plans", "Working Time")}</th>
                  <th style={{ width: "12.5%" }}>{getTranslation(setting, "Route Plans", "Waiting Time")}</th>
                  <th style={{ width: "12.5%" }}>{getTranslation(setting, "Route Plans", "Working %")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{(routeClicked.shiftStart || "00:00") + " - " + (routeClicked.shiftEnd || "N/A")}</td>
                  <td>{length || 0}</td>
                  <td>{distance} km</td>
                  <td>{convertMinutesToTime(travelMins)}</td>
                  <td>{convertMinutesToTime(serviceMins)}</td>
                  <td>{convertMinutesToTime(workingMins)}</td>
                  <td>{convertMinutesToTime(waitingMins)}</td>
                  <td>{workingPercentage}%</td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="content-main">
        <table>
          <thead>
            <tr>
              <th style={{ width: "10%" }}>{getTranslation(setting, "Orders", "Order ID")}</th>
              <th style={{ width: "8%" }}>{getTranslation(setting, "Orders", "Status")}</th>
              <th>{getTranslation(setting, "Orders", "Customer Name")}</th>
              <th style={{ width: "14%" }}>{getTranslation(setting, "Orders", "Address")}</th>
              <th style={{ width: "8%" }}>{getTranslation(setting, "Orders", "Job Type")}</th>
              <th style={{ width: "9%" }}>{getTranslation(setting, "Orders", "Time Window")}</th>
              <th style={{ width: "8%" }}>{getTranslation(setting, "Orders", "Arv. Time")}</th>
              <th style={{ width: "8%" }}>{getTranslation(setting, "Orders", "Fin. Time")}</th>
              <th style={{ width: "8%" }}>{getTranslation(setting, "Route Plans", "Travel Time")}</th>
              <th style={{ width: "9%" }}>{getTranslation(setting, "Route Plans", "Service Time")}</th>
              <th style={{ width: "9%" }}>{getTranslation(setting, "Route Plans", "Working Time")}</th>
              <th style={{ width: "9%" }}>{getTranslation(setting, "Route Plans", "Waiting Time")}</th>
            </tr>
          </thead>
          <tbody>
            {newSteps.map((step, index) => {
              const stop = step;
              const travelMins = step?.travelMins || "-";
              const serviceMins = step?.duration || "-";
              const waitingMins = step?.waitingMins || "-";
              const workingMins = step?.workingMins || "-";
              const status = STATUS.find(s => s.code === stop?.status);

              const endTime = convertToInt(stop.endTime);
              const finishTime = convertToInt(stop.finishTime);
              const arrivalTimeSecs = convertToInt(step?.arrivalTime);
              const isLate =
                (finishTime >= 0 && endTime >= 0 && finishTime > endTime) ||
                (stop.endTime && arrivalTimeSecs > endTime);

              return (
                <tr key={index} onClick={() => onClickStop(stop)} style={{ cursor: "pointer" }}>
                  <td style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        border: `1px solid #${driver?.color || defaultColor}`,
                        color: `#${driver?.color || defaultColor}`,
                        textAlign: "center",
                        minWidth: "24px",
                        minHeight: "24px",
                        lineHeight: "22px",
                        fontSize: "14px",
                        borderRadius: stop.type === "pickup" ? "50%" : 4,
                      }}>
                      {index + 1}
                    </span>
                    <a
                      href={`/orders/${stop.orderId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                      {stop.orderRef || stop.orderCode}
                    </a>
                  </td>
                  <td>
                    <span className={`status orderStatus ${status?.code?.toLowerCase()}`}>
                      {getTranslation(setting, "Orders", status?.code)}
                    </span>
                  </td>
                  <td>{stop.name}</td>
                  <td>
                    <div className="address">{stop.address}</div>
                  </td>
                  <td style={{ textTransform: "capitalize" }}>{getTranslation(setting, "Orders", stop.type)}</td>
                  <td>{(stop.startTime || "") + " - " + (stop.endTime || "")}</td>
                  <td style={{ color: isLate ? "red" : "inherit" }}>{step?.arrivalTime}</td>
                  <td style={{ color: isLate ? "red" : "inherit" }}>{step?.finishTime}</td>
                  <td>{convertMinutesToTime(travelMins)}</td>
                  <td>{convertMinutesToTime(serviceMins)}</td>
                  <td>{convertMinutesToTime(workingMins)}</td>
                  <td>{convertMinutesToTime(waitingMins)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ArrowRight = () => (
  <svg viewBox="0 0 448 512">
    <path
      fill="currentColor"
      d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path>
  </svg>
);

export const IconArrowRight = (
  <Icon component={ArrowRight} style={{ width: 12, marginLeft: 6, verticalAlign: "middle" }} />
);

const DetailOrder = () => (
  <svg viewBox="0 0 12 14">
    <path
      fill="currentColor"
      d="M2.66667 9.99984H7.33333V11.3332H2.66667V9.99984ZM2.66667 7.33317H9.33333V8.6665H2.66667V7.33317ZM2.66667 4.6665H9.33333V5.99984H2.66667V4.6665ZM10.6667 1.99984H7.88C7.6 1.2265 6.86667 0.666504 6 0.666504C5.13333 0.666504 4.4 1.2265 4.12 1.99984H1.33333C1.24 1.99984 1.15333 2.0065 1.06667 2.0265C0.806667 2.07984 0.573333 2.21317 0.393333 2.39317C0.273333 2.51317 0.173333 2.65984 0.106667 2.81984C0.0400001 2.97317 0 3.1465 0 3.33317V12.6665C0 12.8465 0.0400001 13.0265 0.106667 13.1865C0.173333 13.3465 0.273333 13.4865 0.393333 13.6132C0.573333 13.7932 0.806667 13.9265 1.06667 13.9798C1.15333 13.9932 1.24 13.9998 1.33333 13.9998H10.6667C11.4 13.9998 12 13.3998 12 12.6665V3.33317C12 2.59984 11.4 1.99984 10.6667 1.99984ZM6 1.83317C6.27333 1.83317 6.5 2.05984 6.5 2.33317C6.5 2.6065 6.27333 2.83317 6 2.83317C5.72667 2.83317 5.5 2.6065 5.5 2.33317C5.5 2.05984 5.72667 1.83317 6 1.83317ZM10.6667 12.6665H1.33333V3.33317H10.6667V12.6665Z"></path>
  </svg>
);

export const MoveToInbox = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.6933 3.48667L12.7667 2.36667C12.5867 2.14 12.3133 2 12 2H4C3.68667 2 3.41333 2.14 3.22667 2.36667L2.30667 3.48667C2.11333 3.71333 2 4.01333 2 4.33333V12.6667C2 13.4 2.6 14 3.33333 14H12.6667C13.4 14 14 13.4 14 12.6667V4.33333C14 4.01333 13.8867 3.71333 13.6933 3.48667ZM4.16 3.33333H11.84L12.3933 4H3.61333L4.16 3.33333ZM3.33333 12.6667V5.33333H12.6667V12.6667H3.33333ZM5.33333 9.33333H7.03333V11.3333H8.96667V9.33333H10.6667L8 6.66667L5.33333 9.33333Z"
      fill="var(--primary-color)"
    />
  </svg>
);

export const IconDetailOrder = <Icon component={DetailOrder} style={{ width: 12, marginLeft: 6 }} />;
